import React, { useEffect, useRef, useState } from "react";
import createGlobe from "cobe";

/**
 * CobeGlobe — rotating WebGL globe using cobe's native marker system.
 *
 * Markers are passed directly to cobe's `markers` option as
 * [{ location: [lat, lng], size }] in raw degrees. cobe's own shader
 * projects them onto the sphere surface, so they always sit exactly on
 * the globe at any screen size — no custom 3D-to-2D overlay math required.
 */
export default function CobeGlobe({
  markers,
  rotationSpeed = 0.006,
  ariaLabel = "Rotating globe showing Digital24's global coverage",
  maxWidth = "260px",
  markerColor = [0.45, 0.0, 0.8],
  glowColor = [0.13, 0.11, 0.22],
}) {
  const canvasRef = useRef(null);
  const globeRef = useRef(null);
  const phiRef = useRef(0);
  const observerRef = useRef(null);
  const resizeTimerRef = useRef(null);
  const speedRef = useRef(rotationSpeed);
  speedRef.current = rotationSpeed;
  const reducedRef = useRef(false);

  const [reduced, setReduced] = useState(false);
  reducedRef.current = reduced;

  // --- Reduced-motion detection ---
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  // --- cobe globe with native markers ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const initGlobe = (sizePx) => {
      if (globeRef.current) {
        globeRef.current.destroy();
        globeRef.current = null;
      }
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const dim = Math.max(1, Math.round(sizePx * dpr));
      let phi = phiRef.current;

      globeRef.current = createGlobe(canvas, {
        devicePixelRatio: dpr,
        width: dim,
        height: dim,
        phi: phi + 0.25,
        theta: 0.25,
        dark: 1,
        diffuse: 1.2,
        mapSamples: 16000,
        mapBrightness: 6,
        baseColor: [0.20, 0.21, 0.28],
        markerColor,
        glowColor,
        markers: markers || [],
        onRender: (state) => {
          if (!reducedRef.current) {
            phi += speedRef.current;
            state.phi = phi;
            phiRef.current = phi;
          }
        },
      });
    };

    const maybeInit = () => {
      const width = canvas.clientWidth;
      if (width > 0) initGlobe(width);
    };

    maybeInit();

    observerRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width <= 0) return;
        if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = setTimeout(() => {
          const currentWidth = canvas.clientWidth;
          if (currentWidth > 0) initGlobe(currentWidth);
        }, 150);
      }
    });
    observerRef.current.observe(canvas);

    return () => {
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
      observerRef.current?.disconnect();
      globeRef.current?.destroy();
      globeRef.current = null;
    };
  }, [reduced, markers]);

  return (
    <div
      className="relative w-full aspect-square -translate-y-[10%]"
      style={{ maxWidth }}
    >
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={ariaLabel}
        className="w-full h-full"
      />
    </div>
  );
}